import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import compliance from 'data/compliance.json'
import ReviewNotice from './review-notice'

/**
 * Shared shell for every student protection and compliance page, so the pages
 * keep the same typography, spacing and metadata handling as the rest of the site.
 */
function PolicyPage({ title, description, intro, children }) {
  useEffect(() => {
    document.title = `UHDSS - ${title}`
    window.scrollTo(0, 0)
  }, [title])

  return (
    <main id='main' className='mt-5'>
      <Helmet>
        <title>{`UHDSS - ${title}`}</title>
        <meta name='description' content={description} />
      </Helmet>

      <section className='pt-5 bg-gradient-light-white pb-0'>
        <div className='container'>
          <div className='row justify-content-center'>
            <div className='col-12 col-md-10 col-lg-9' data-aos='fade-up'>
              <span className='badge rounded-pill bg-success-soft mb-3'>
                <span className='h6 text-uppercase'>Student protection and compliance</span>
              </span>

              <h1 className='mb-4 display-6 dark-blue'>{title}</h1>

              {intro && <p className='fs-5 dark-blue lh-base'>{intro}</p>}

              <p className='text-muted fs-6 mb-0'>Last updated: {compliance.meta.lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      <section className='pt-4 pb-5'>
        <div className='container'>
          <div className='row justify-content-center'>
            <div className='col-12 col-md-10 col-lg-9'>
              <ReviewNotice />
              {children}
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

PolicyPage.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  intro: PropTypes.string,
  children: PropTypes.node
}

/** A titled block of policy text, used for every section of a policy page. */
export function PolicySection({ title, children }) {
  return (
    <div className='mb-5'>
      <h2 className='h4 dark-blue mb-3'>{title}</h2>
      {children}
    </div>
  )
}

PolicySection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node
}

/** A simple bulleted list matching the body text style of the policy pages. */
export function PolicyList({ items }) {
  return (
    <ul className='dark-blue fs-6'>
      {items.map((item, index) => (
        <li className='mb-2' key={index}>{item}</li>
      ))}
    </ul>
  )
}

PolicyList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired
}

export default PolicyPage
